import React, { useEffect, useState } from "react";
import { Eye, Plus, ShieldCheck, Trash2 } from "lucide-react";
import { fetchOrganizationRolePermissions, deleteOrganizationRolePermissions } from "../../../Apis/ApplicationManagementApis";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import usePermissionDataStore from "../../../Zustand/Store/usePermissionDataStore";
import { hasPermissionAction } from "../../../Utils/organizationPermissions";

function RolePermissionList() {
  const [assignment, setAssignment] = useState([]);
  const [loading, setLoading] = useState(true);
  const { userData } = useAuthStore();
  const { Permission } = usePermissionDataStore();
  const org = userData?.organization;
  const navigate = useNavigate();

  const canAdd = hasPermissionAction(Permission, "USER_ROLE_PERMISSION_ADD");
  const canEdit = hasPermissionAction(Permission, "USER_ROLE_PERMISSION_EDIT");
  const canDelete = hasPermissionAction(Permission, "USER_ROLE_PERMISSION_DELETE");

  const ROLE_PERMISSIONS_IN_USE_MSG =
    "These permissions are assigned to users through this role and cannot be deleted. Unassign the role from all users first.";

  const deleteRolePermission = async (item) => {
    if (!canDelete || !org?.organization_id) return;
    if (item?.assigned_to_users) {
      toast.error(ROLE_PERMISSIONS_IN_USE_MSG);
      return;
    }
    if (
      !window.confirm(
        "Delete all permissions for this role? This cannot be undone."
      )
    ) {
      return;
    }

    try {
      await deleteOrganizationRolePermissions(org.organization_id, item.id);
      toast.success("Role permissions deleted successfully");
      setAssignment((prev) => prev.filter((row) => row.id !== item.id));
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to delete role permissions"
      );
    }
  };

  useEffect(() => {
    if (!org?.organization_id) { setLoading(false); return; }
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await fetchOrganizationRolePermissions(org.organization_id);
        const a = Array.isArray(res?.permissions)
          ? res.permissions
          : Array.isArray(res?.permissions?.data)
          ? res.permissions.data
          : [];

        const assignedRoleIds = new Set(
          (Array.isArray(res?.assigned_role_ids) ? res.assigned_role_ids : []).map(
            (id) => String(id)
          )
        );

        const grouped = a.reduce((acc, curr) => {
          const roleId = curr.organization_user_role?.organization_user_role_id ?? curr.organization_user_role_id;
          if (!roleId || acc[roleId]) return acc;
          acc[roleId] = {
            id: roleId,
            assigned_to_users: assignedRoleIds.has(String(roleId)),
            name:
              curr.organization_user_role?.user_role_name ??
              curr.organization_user_role?.role_name ??
              curr.application_user_role?.user_role_name,
          };
          return acc;
        }, {});

        setAssignment(Object.values(grouped));
      } catch (error) {
        console.error(error);
        toast.error("Failed to fetch role permissions");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [org?.organization_id]);

  return (
    <div className="ems-fade-up">
      <div className="ems-page-head">
        <div>
          <h1 className="ems-page-title">User Role Permissions</h1>
          <div className="ems-page-sub">
            <span>User management</span>
            <span className="ems-dot" />
            <span>{assignment.length} roles configured</span>
          </div>
        </div>
        <div className="ems-head-actions">
          {canAdd && (
            <button className="ems-btn ems-primary" onClick={() => navigate("/application/user-role-permission/add")}>
              <Plus size={16} />
              Add Role Permission
            </button>
          )}
        </div>
      </div>

      <div className="ems-section-divider" />

      <div className="ems-card ems-table-card">
        {loading ? (
          <div style={{ padding: "48px 0", textAlign: "center", color: "var(--ems-faint)" }}>
            Loading…
          </div>
        ) : assignment.length === 0 ? (
          <div style={{ padding: "48px 0", textAlign: "center", color: "var(--ems-faint)", fontSize: 14 }}>
            No role permissions configured yet.
          </div>
        ) : (
          <div className="ems-table-wrap">
            <table className="ems-data">
              <thead>
                <tr>
                  <th>Role</th>
                  <th style={{ width: 240 }}>View</th>
                  <th className="ems-num" style={{ width: 140 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {assignment.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div className="ems-act-cell">
                        <span className="ems-src-ic">
                          <ShieldCheck size={16} />
                        </span>
                        <span className="ems-lead-name">{item.name}</span>
                      </div>
                    </td>
                    <td>
                      <button
                        className="ems-btn-view"
                        onClick={() => navigate(`/application/user-role-permission/view/${item.id}`)}
                      >
                        <Eye size={15} />
                        View Permissions
                      </button>
                    </td>
                    <td className="ems-num">
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          justifyContent: "flex-end",
                          alignItems: "center",
                        }}
                      >
                        {canEdit && (
                          <button
                            className="ems-link-edit"
                            onClick={() =>
                              navigate(
                                `/application/user-role-permission/edit/${item.id}`
                              )
                            }
                          >
                            Edit
                          </button>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            className="ems-icon-link danger"
                            title={
                              item.assigned_to_users
                                ? ROLE_PERMISSIONS_IN_USE_MSG
                                : "Delete"
                            }
                            disabled={item.assigned_to_users}
                            style={
                              item.assigned_to_users
                                ? { opacity: 0.4, cursor: "not-allowed" }
                                : undefined
                            }
                            onClick={() => deleteRolePermission(item)}
                          >
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default RolePermissionList;
